import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalendarAlt, faFileAlt, faEye, faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import moment from 'moment';
import '../styles/BOLs.css';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import DocumentViewerModal from './DocumentViewerModal';
import CreateBOLWizard from './CreateBOLWizard';

function BOLs() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openBOLs, setOpenBOLs] = useState([]);
  const [completedBOLs, setCompletedBOLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBOL, setSelectedBOL] = useState(null);
  const [bolDetails, setBolDetails] = useState({
    origin: {},
    destination: {},
    stops: [],
    freight: [],
    rates: [],
    documents: []
  });
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  
  // Document viewer modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);
  
  const mapRef = useRef(null);
  
  // Map constants - SVG icons for map markers
  const svgIcons = {
    pickupSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="blue" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0S384 86 384 192z"/></svg>',
    dropSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="green" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0S384 86 384 192z"/></svg>',
    pulsingSVGPick: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 100 100"><circle cx="50" cy="50" r="35" fill="none" stroke="#1a73e8" stroke-width="10"><animate attributeName="r" from="35" to="50" dur="1s" begin="0s" repeatCount="indefinite" /><animate attributeName="opacity" from="1" to="0" dur="1s" begin="0s" repeatCount="indefinite" /></circle><circle cx="50" cy="50" r="10" fill="#030bfc" /></svg>',
    pulsingSVGDrop: '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 100 100"><circle cx="50" cy="50" r="35" fill="none" stroke="#03590b" stroke-width="10"><animate attributeName="r" from="35" to="50" dur="1s" begin="0s" repeatCount="indefinite" /><animate attributeName="opacity" from="1" to="0" dur="1s" begin="0s" repeatCount="indefinite" /></circle><circle cx="50" cy="50" r="10" fill="#03590b" /></svg>',
    inactiveSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="grey" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0S384 86 384 192z"/></svg>',
    stoppedTruckSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#050505" d="M48 0C21.5 0 0 21.5 0 48V368c0 26.5 21.5 48 48 48H64c0 53 43 96 96 96s96-43 96-96H384c0 53 43 96 96 96s96-43 96-96h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V288 256 237.3c0-17-6.7-33.3-18.7-45.3L512 114.7c-12-12-28.3-18.7-45.3-18.7H416V48c0-26.5-21.5-48-48-48H48zM416 160h50.7L544 237.3V256H416V160zM112 416a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" stroke="white" stroke-width="15" /></svg>',
    movingTruckSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#057ef7" d="M48 0C21.5 0 0 21.5 0 48V368c0 26.5 21.5 48 48 48H64c0 53 43 96 96 96s96-43 96-96H384c0 53 43 96 96 96s96-43 96-96h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V288 256 237.3c0-17-6.7-33.3-18.7-45.3L512 114.7c-12-12-28.3-18.7-45.3-18.7H416V48c0-26.5-21.5-48-48-48H48zM416 160h50.7L544 237.3V256H416V160zM112 416a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" stroke="black" stroke-width="15" /></svg>',
    idleTruckSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#828385" d="M48 0C21.5 0 0 21.5 0 48V368c0 26.5 21.5 48 48 48H64c0 53 43 96 96 96s96-43 96-96H384c0 53 43 96 96 96s96-43 96-96h32c17.7 0 32-14.3 32-32s-14.3-32-32-32V288 256 237.3c0-17-6.7-33.3-18.7-45.3L512 114.7c-12-12-28.3-18.7-45.3-18.7H416V48c0-26.5-21.5-48-48-48H48zM416 160h50.7L544 237.3V256H416V160zM112 416a48 48 0 1 1 96 0 48 48 0 1 1 -96 0zm368-48a48 48 0 1 1 0 96 48 48 0 1 1 0-96z" stroke="black" stroke-width="15" /></svg>',
    trailerSVG: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#f7a202" d="M48 32C21.5 32 0 53.5 0 80V336c0 26.5 21.5 48 48 48H65.1c7.8-54.3 54.4-96 110.9-96s103.1 41.7 110.9 96H488h8H608c17.7 0 32-14.3 32-32s-14.3-32-32-32H544V80c0-26.5-21.5-48-48-48H48zM80 96c8.8 0 16 7.2 16 16l0 131.2c-11.4 5.9-22.2 12.9-32 21V112c0-8.8 7.2-16 16-16zm96 128c-5.4 0-10.7 .2-16 .7L160 112c0-8.8 7.2-16 16-16s16 7.2 16 16l0 112.7c-5.3-.5-10.6-.7-16-.7zm80 19.2L256 112c0-8.8 7.2-16 16-16s16 7.2 16 16l0 152.2c-9.8-8.1-20.6-15.2-32-21zM368 96c8.8 0 16 7.2 16 16l0 192c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-192c0-8.8 7.2-16 16-16zm112 16l0 192c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-192c0-8.8 7.2-16 16-16s16 7.2 16 16zM176 480a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-112a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"/></svg>'
  };

  // Map related state
  const [mapDataSources, setMapDataSources] = useState({
    bolOverlay: null,
    driverOverlay: null,
    routeOverlay: null,
    pulsingOverlay: null
  });

  const [mapLayers, setMapLayers] = useState({
    bolLayer: null,
    driverLayer: null,
    routeLayer: null,
    pulsingLayer: null
  });

  const [popup, setPopup] = useState(null);

  // Add these state variables after other useState declarations
  const [hoveredBOL, setHoveredBOL] = useState(null);
  const [highlightOverlay, setHighlightOverlay] = useState(null);

  // Add a new state for the BOL PDF
  const [bolPdfData, setBolPdfData] = useState(null);

  // Add state for BOL creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);



  useEffect(() => {
    // Mobile detection
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check initial screen size
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Load open BOLs
    loadOpenBOLs();
    
    // Load completed BOLs with date range
    loadCompletedBOLs();
    
    // Initialize map only on desktop (not mobile)
    if (window.atlas && window.innerWidth > 768) {
      initializeMap();
    }
    
    // Cleanup resize listener
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  const loadOpenBOLs = async () => {
    try {
      setLoading(true);
      console.log("Fetching open BOLs...");
      const response = await fetch(`${API_URL}/api/bols/open`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Open BOLs response:", data);
      setOpenBOLs(data);
      
      // Update map with BOL locations if map is initialized
      if (mapRef.current && window.atlas) {
        populateMap(response.data);
      }
    } catch (error) {
      console.error("Failed to load open BOLs:", error);
      toast.error(`Failed to load open BOLs: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const loadCompletedBOLs = async () => {
    try {
      setLoading(true);
      console.log("Fetching completed BOLs...");
      
      // Make sure dates are in the correct format
      const formattedStartDate = moment(dateRange.startDate).format('YYYY-MM-DD');
      const formattedEndDate = moment(dateRange.endDate).format('YYYY-MM-DD');
      
      console.log("Date range for API request:", { startDate: formattedStartDate, endDate: formattedEndDate });
      
      const url = new URL(`${API_URL}/api/bols/completed`);
      url.searchParams.append('startDate', formattedStartDate);
      url.searchParams.append('endDate', formattedEndDate);
      url.searchParams.append('_t', new Date().getTime().toString());
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Completed BOLs response:", data);
      setCompletedBOLs(data);
    } catch (error) {
      console.error("Failed to load completed BOLs:", error);
      toast.error(`Failed to load completed BOLs: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };
  
  const handleApplyDateFilter = () => {
    if (dateRange.startDate <= dateRange.endDate) {
      loadCompletedBOLs();
    } else {
      toast.error("Start date must be before end date");
    }
  };
  
  const handleBOLClick = async (bol) => {
    try {
      setSelectedBOL(bol);
      
      // Load BOL details
      const detailsResponse = await fetch(`${API_URL}/api/bols/${bol.idbols}/details`, {
        credentials: 'include'
      });
      
      if (!detailsResponse.ok) {
        throw new Error(`HTTP error! status: ${detailsResponse.status}`);
      }
      
      const detailsData = await detailsResponse.json();
      setBolDetails(detailsData);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Failed to load BOL details:", error);
      toast.error(`Failed to load BOL details: ${error.message || 'Unknown error'}`);
    }
  };
  
  const handlePrintBOL = async () => {
    if (!selectedBOL) return;
    
    try {
      // Show loading state
      toast.loading("Generating BOL document...");
      
      // Call API to generate BOL PDF
      const response = await fetch(`${API_URL}/api/bols/${selectedBOL.idbols}/print`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          includePOD: selectedBOL.idBOLStatus >= 4 // Include POD info only for completed BOLs
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get content type from response
      const contentType = response.headers.get('content-type');
      console.log("BOL PDF content type:", contentType);
      
      // Create a blob URL for the PDF
      const responseBlob = await response.blob();
      const blob = new Blob([responseBlob], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      
      // Prepare document data for the viewer
      const bolDocument = {
        descr: `BOL ${selectedBOL.idbols}`,
        fileName: `BOL_${selectedBOL.idbols}.pdf`,
        blobUrl: blobUrl, // Use blob URL instead of base64
        type: contentType || 'application/pdf'
      };
      
      // Store the BOL PDF data
      setBolPdfData(bolDocument);
      
      // Close loading toast
      toast.dismiss();
      
      // Show success message
      toast.success("BOL generated successfully");
      
      // Show the document in the viewer modal without closing the BOL details
      setSelectedDocument(bolDocument);
      setShowDocumentModal(true);
    } catch (error) {
      console.error("Failed to print BOL:", error);
      toast.dismiss();
      toast.error(`Failed to print BOL: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle document click to open in modal
  const handleDocumentClick = async (doc, e) => {
    e.preventDefault();
    
    // Show loading toast
    toast.loading("Loading document...");
    
    try {
      // If we have a blobUrl, use it directly
      if (doc.blobUrl) {
        // Prepare document data for the viewer
        const documentData = {
          ...doc,
          url: doc.blobUrl
        };
        
        // Dismiss loading toast
        toast.dismiss();
        
        // Show the document in the modal
        setSelectedDocument(documentData);
        setShowDocumentModal(true);
      } else {
        // Fallback to fetching the document
        const response = await fetch(`${API_URL}/api/bols/documents/${doc.fileName}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Convert blob to base64 to display in the document viewer
        const responseBlob = await response.blob();
        const reader = new FileReader();
        reader.readAsDataURL(responseBlob);
        reader.onloadend = () => {
          const base64data = reader.result;
          
          // Prepare document data for the viewer with base64 content
          const documentWithData = {
            ...doc,
            fileData: base64data
          };
          
          // Dismiss loading toast
          toast.dismiss();
          
          // Show the document in the modal
          setSelectedDocument(documentWithData);
          setShowDocumentModal(true);
        };
      }
    } catch (error) {
      console.error("Failed to load document:", error);
      toast.dismiss();
      toast.error(`Failed to load document: ${error.message || 'Unknown error'}`);
    }
  };
  
  // Map initialization and handling
  const initializeMap = () => {
    // Create the map instance
    const map = new window.atlas.Map('azureMap', {
      center: [-80.854, 35.221], // Center on Charlotte, NC
      zoom: 7,
      language: 'en-US',
      renderWorldCopies: false,
      showLogo: false,
      showFeedbackLink: false,
      style: 'grayscale_light',
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey: import.meta.env.VITE_AZURE_MAPS_KEY || '123' // Use Vite environment variable with fallback
      }
    });
    
    // Store map reference
    mapRef.current = map;
    
    // Create popup for displaying information
    const mapPopup = new window.atlas.Popup({
      pixelOffset: [0, -30], // Offset to position popup above the marker
      closeButton: true      // Show a close button on the popup
    });
    setPopup(mapPopup);
    
    // Set up map ready event
    map.events.add('ready', () => {
      console.log('Map is ready');
      
      // Initialize map data sources and layers
      setupMapComponents(map).then(() => {
        // If we already have data, populate the map
        if (openBOLs.length > 0) {
          populateMap(openBOLs);
        }
      });
    });
  };
  
  // Setup map layers and data sources
  const setupMapComponents = async (map) => {
    // Create the data sources
    const bolOverlay = new window.atlas.source.DataSource("bolOverlay", {
      cluster: false,
      clusterMaxZoom: 0,
      clusterRadius: 0.1,
    });
    
    const driverOverlay = new window.atlas.source.DataSource("driverOverlay", {
      cluster: false,
      clusterMaxZoom: 0,
      clusterRadius: 45,
    });
    
    const routeOverlay = new window.atlas.source.DataSource("routeOverlay", {});
    
    const pulsingOverlay = new window.atlas.source.DataSource("pulsingOverlay", {});
    
    // Create the icon images
    await Promise.all([
      map.imageSprite.add("pickIcon", "data:image/svg+xml," + encodeURIComponent(svgIcons.pickupSVG)),
      map.imageSprite.add("dropIcon", "data:image/svg+xml," + encodeURIComponent(svgIcons.dropSVG)),
      map.imageSprite.add("pulsingIconDrop", "data:image/svg+xml," + encodeURIComponent(svgIcons.pulsingSVGDrop)),
      map.imageSprite.add("pulsingIconPick", "data:image/svg+xml," + encodeURIComponent(svgIcons.pulsingSVGPick)),
      map.imageSprite.add("inactiveIcon", "data:image/svg+xml," + encodeURIComponent(svgIcons.inactiveSVG)),
      map.imageSprite.add("trailerIcon", "data:image/svg+xml," + encodeURIComponent(svgIcons.trailerSVG)),
      map.imageSprite.add("stoppedTruck", "data:image/svg+xml," + encodeURIComponent(svgIcons.stoppedTruckSVG)),
      map.imageSprite.add("movingTruck", "data:image/svg+xml," + encodeURIComponent(svgIcons.movingTruckSVG)),
      map.imageSprite.add("idleTruck", "data:image/svg+xml," + encodeURIComponent(svgIcons.idleTruckSVG)),
      map.imageSprite.createFromTemplate("pickPin", "pin", "#030bfc", "transparent", 0.8),
      map.imageSprite.createFromTemplate("dropPin", "pin", "#03590b", "transparent", 0.7),
      map.imageSprite.createFromTemplate("inactivePick", "pin-round", "transparent", "#03cefc", 0.7),
      map.imageSprite.createFromTemplate("inactiveDrop", "pin-round", "transparent", "#06c726", 0.7),
      map.imageSprite.createFromTemplate("flag", "flag-triangle", "#ff6161", "#ff6161", 0.7),
      map.imageSprite.add("pickIconPulse", "data:image/svg+xml," + encodeURIComponent(svgIcons.pulsingSVGPick)),
      map.imageSprite.add("dropIconPulse", "data:image/svg+xml," + encodeURIComponent(svgIcons.pulsingSVGDrop)),
    ]);
    
    // Create the layers
    const bolLayer = new window.atlas.layer.SymbolLayer(bolOverlay, "BOLs", {
      iconOptions: {
        image: [
          "case",
          ["==", ["get", "type"], "pick"],
          "pickPin",
          ["==", ["get", "type"], "inactivePick"],
          "inactivePick",
          ["==", ["get", "type"], "drop"],
          "dropPin",
          ["==", ["get", "type"], "inactiveDrop"],
          "inactiveDrop",
          "",
        ],
        offset: [0, 0],
        allowOverlap: true,
        ignorePlacement: true,
      },
    });
    
    const driverLayer = new window.atlas.layer.SymbolLayer(driverOverlay, "Drivers", {
      minZoom: 0,
      maxZoom: 24,
      iconOptions: {
        image: [
          "case",
          ["==", ["get", "status"], "Stop"],
          "stoppedTruck",
          ["==", ["get", "status"], "Idle"],
          "idleTruck",
          "movingTruck",
        ],
        size: 0.11,
        allowOverlap: true,
        ignorePlacement: true,
        anchor: "left",
        offset: [-2, 0],
      },
    });
    
    const routeLayer = new window.atlas.layer.LineLayer(routeOverlay, "Routes", {
      strokeColor: "#2272B9",
      strokeWidth: 5,
      lineJoin: "round",
      lineCap: "round"
    });
    
    const pulsingLayer = new window.atlas.layer.SymbolLayer(pulsingOverlay, "idLayer", {
      iconOptions: {
        image: "pulsingIcon",
        size: 1,
      },
    });
    
    // Create highlight overlay for hover effects
    const highlightDataSource = new window.atlas.source.DataSource("highlightOverlay", {
      cluster: false,
      clusterMaxZoom: 0,
      clusterRadius: 0.1,
    });
    
    // Store reference to highlight overlay
    setHighlightOverlay(highlightDataSource);
    
    // Add sources to map
    map.sources.add(bolOverlay);
    map.sources.add(driverOverlay);
    map.sources.add(routeOverlay);
    map.sources.add(pulsingOverlay);
    map.sources.add(highlightDataSource);
    
    // Create highlight layer
    const highlightLayer = new window.atlas.layer.SymbolLayer(highlightDataSource, "highlight", {
      iconOptions: {
        image: [
          "case",
          ["==", ["get", "type"], "pickHighlight"],
          "pickIconPulse",
          ["==", ["get", "type"], "dropHighlight"],
          "dropIconPulse",
          "",
        ],
        size: 1.0,
        offset: [0, 0],
        allowOverlap: true,
        ignorePlacement: true,
      },
      filter: ['any', ['==', ['get', 'type'], 'pickHighlight'], ['==', ['get', 'type'], 'dropHighlight']]
    });
    
    // Add layers to map
    map.layers.add([bolLayer, driverLayer, routeLayer, pulsingLayer, highlightLayer]);
    
    // Save references to data sources and layers
    setMapDataSources({
      bolOverlay,
      driverOverlay,
      routeOverlay,
      pulsingOverlay
    });
    
    setMapLayers({
      bolLayer,
      driverLayer,
      routeLayer,
      pulsingLayer
    });
    
    // Add click events for layers
    addMapEventListeners(map, bolLayer, driverLayer, routeLayer);
  };
  
  // Add click event listeners for map layers
  const addMapEventListeners = (map, bolLayer, driverLayer, routeLayer) => {
    // Add BOL layer click event
    map.events.add("click", bolLayer, (e) => {
      if (e.shapes && e.shapes.length > 0 && e.shapes[0] instanceof window.atlas.Shape) {
        const properties = e.shapes[0].getProperties();
        let label = properties.type;
        
        switch (label) {
          case "pick":
            label = "Pickup";
            break;
          case "drop":
            label = "Dropoff";
            break;
          case "inactivePick":
            label = "Future Pickup";
            break;
          case "inactiveDrop":
            label = "Future Dropoff";
            break;
        }
        
        const content = `
          <div class='m-0 p-0 ps-1 pe-1'>
            <p class='m-0 smallText2'><b>Type:</b> ${label}</p>
            <p class='m-0 smallText2'><b>Leg:</b> ${properties.id} <b>Pro:</b> ${properties.pro}</p>
            <p class='m-0 smallText2'><b>Origin:</b> ${properties.origin}</p>
            <p class='m-0 smallText2'><b>Origin Date:</b> ${formatDate(properties.originDate)}</p>
            <p class='m-0 smallText2'><b>Destination:</b> ${properties.dest}</p>
            <p class='m-0 smallText2'><b>Destination Date:</b> ${formatDate(properties.destDate)}</p>
          </div>
        `;
        
        if (popup) {
          popup.setOptions({
            content: `<pre>${content}</pre>`,
            position: e.shapes[0].getCoordinates(),
          });
          
          popup.open(map);
        }
      }
    });
    
    // Add route layer click event
    map.events.add("click", routeLayer, (e) => {
      if (e.shapes && e.shapes.length > 0 && e.shapes[0] instanceof window.atlas.Shape) {
        const properties = e.shapes[0].getProperties();
        
        let dateTime = properties.summary.arrivalTime;
        let timeWithSeconds = dateTime.split('T')[1].split('-')[0];
        let timeWithoutSeconds = timeWithSeconds.slice(0, 5);
        let travelTimeInMinutes = Math.round(properties.summary.travelTimeInSeconds / 60);
        
        const content = `
          <div class='m-0 p-0 ps-1 pe-1'>
            <p class='m-0 smallText2'><b>PRO:</b> ${properties.id}</p>
            <p class='m-0 smallText2'><b>Enroute To:</b> ${properties.name}</p>
            <p class='m-0 smallText2'><b>ETA:</b> ${timeWithoutSeconds} ${travelTimeInMinutes} min</p>
          </div>
        `;
        
        if (popup) {
          popup.setOptions({
            content: `<pre>${content}</pre>`,
            position: e.position,
          });
          
          popup.open(map);
        }
      }
    });
    
    // Close popup on map move
    map.events.add("move", () => {
      if (popup) {
        popup.close();
      }
    });
  };
  
  // Populate map with BOL data
  const populateMap = (bolsData) => {
    if (!mapRef.current || !mapDataSources.bolOverlay || !mapDataSources.driverOverlay) return;
    
    // Clear existing data
    mapDataSources.bolOverlay.clear();
    mapDataSources.driverOverlay.clear();
    mapDataSources.routeOverlay.clear();
    
    const positions = [];
    
    // Loop through BOLs to add map features
    bolsData.forEach(bol => {
      if (!bol) return;
      
      // Get coordinates from BOL data
      const fromGeo = bol.fromGeo ? bol.fromGeo.split(",") : null;
      const toGeo = bol.toGeo ? bol.toGeo.split(",") : null;
      let driverLoc = null;
      
      if (fromGeo && toGeo) {
        // Check for valid coordinates
        if (isNaN(fromGeo[0]) || isNaN(fromGeo[1]) || isNaN(toGeo[0]) || isNaN(toGeo[1])) {
          console.error("Invalid coordinates:", bol.fromGeo, bol.toGeo);
          return;
        }
        
        // Add pickup point if appropriate
        const today = moment().format("YYYY-MM-DD");
        const bStatus = [1, 2, 3, 7];
        
        if (bStatus.includes(parseInt(bol.min_idstatus))) {
          positions.push([parseFloat(fromGeo[1]), parseFloat(fromGeo[0])]);
          
          const pickPoint = new window.atlas.data.Point([
            parseFloat(fromGeo[1]),
            parseFloat(fromGeo[0]),
          ]);
          
          let pickType = "pick";
          if (bol.PickDate > today) {
            pickType = "inactivePick";
          }
          
          const pickFeature = new window.atlas.data.Feature(pickPoint, {
            type: pickType,
            id: bol.idbols,
            pro: bol.idbols,
            origin: bol.PickName,
            originDate: bol.PickDate,
            dest: bol.DropName,
            destDate: bol.DropDate,
            status: bol.BOLStatus,
          });
          
          mapDataSources.bolOverlay.add(pickFeature);
        }
        
        // Add dropoff point
        const dropPoint = new window.atlas.data.Point([
          parseFloat(toGeo[1]),
          parseFloat(toGeo[0]),
        ]);
        
        let dropType = "drop";
        if (bol.DropDate > today) {
          dropType = "inactiveDrop";
        }
        
        const dropFeature = new window.atlas.data.Feature(dropPoint, {
          type: dropType,
          id: bol.idbols,
          pro: bol.idbols,
          origin: bol.PickName,
          originDate: bol.PickDate,
          dest: bol.DropName,
          destDate: bol.DropDate,
          status: bol.BOLStatus,
        });
        
        mapDataSources.bolOverlay.add(dropFeature);
        positions.push([parseFloat(toGeo[1]), parseFloat(toGeo[0])]);
        
        // Add driver location if available
        if (bol.drvLoc && bol.drvLoc !== ',' && bol.drvLoc !== '') {
          driverLoc = bol.drvLoc.split(",");
          positions.push([parseFloat(driverLoc[1]), parseFloat(driverLoc[0])]);
          
          const driverPoint = new window.atlas.data.Point([
            parseFloat(driverLoc[1]),
            parseFloat(driverLoc[0]),
          ]);
          
          const driverFeature = new window.atlas.data.Feature(driverPoint, {
            type: "movingTruck",
            id: bol.idbols,
            pro: bol.idbols,
            status: bol.BOLStatus,
          });
          
          mapDataSources.driverOverlay.add(driverFeature);
          
          // Get route for driver
          if (bol.min_idstatus == 3) {
            bol.name = bol.PickName;
            getRoute(bol.drvLoc, fromGeo.join(','), bol);
          } else if (bol.max_idstatus == 4 || bol.final_status === 4) {
            bol.name = bol.DropName;
            getRoute(bol.drvLoc, toGeo.join(','), bol);
          }
        }
      }
    });
    
    // Set map bounds to contain all points
    if (positions.length > 0) {
      const mapBounds = window.atlas.data.BoundingBox.fromPositions(positions);
      centerMap(mapBounds);
    }
  };
  
  // Get route between two points
  const getRoute = async (fromGeo, toGeo, bol) => {
    if (!fromGeo || !toGeo || !mapDataSources.routeOverlay) return;
    
    try {
      const routeRequest = {
        "api-version": "1.0",
        query: (fromGeo + ":" + toGeo).replace(/\s+/g, ''),
        travelMode: "truck",
        traffic: true,
        routeType: "shortest",
        language: "en",
        units: "imperial",
        vehicleCommercial: true,
        vehicleMaxSpeed: 65
      };
      
      // Call backend to get route data
      const url = new URL(`${API_URL}/api/bols/route`);
      Object.keys(routeRequest).forEach(key => {
        url.searchParams.append(key, routeRequest[key]);
      });
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const routeData = await response.json();
      const route = getRouteFeaturesFromResponse(routeData.routes);
      
      if (route.features.length > 0) {
        route.features[0].properties.id = bol.idbols;
        route.features[0].properties.name = bol.name;
        mapDataSources.routeOverlay.add(route);
      }
    } catch (error) {
      console.error("Error getting route:", error);
    }
  };
  
  // Convert route data to map features
  const getRouteFeaturesFromResponse = (routes) => {
    const bounds = [];
    const features = routes.map((route, index) => {
      const multiLineCoords = route.legs.map((leg) => {
        return leg.points.map((coord) => {
          const position = [coord.longitude, coord.latitude];
          bounds.push(position);
          return position;
        });
      });
      
      const props = {
        ...route,
        legSummaries: route.legs.map((leg) => leg.summary),
        resultIndex: index
      };
      delete props.legs;
      
      return {
        type: "Feature",
        geometry: {
          type: "MultiLineString",
          coordinates: multiLineCoords
        },
        properties: props
      };
    });
    
    return {
      type: "FeatureCollection",
      features: features,
      bbox: new window.atlas.data.BoundingBox.fromLatLngs(bounds)
    };
  };
  
  // Center map on all features
  const centerMap = (mapBounds) => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    
    if (!mapBounds) {
      // If no bounds provided, calculate from all data sources
      let data = [];
      const sources = map.sources.getSources();
      
      sources.forEach((s) => {
        if (s instanceof window.atlas.source.DataSource) {
          data = data.concat(s.toJson().features);
        }
      });
      
      if (data.length > 0) {
        mapBounds = window.atlas.data.BoundingBox.fromData(data);
      }
    }
    
    if (mapBounds) {
      map.setCamera({
        bounds: mapBounds,
        padding: 40
      });
    }
  };
  
  // Calculate midpoint of coordinates for route popup placement
  const calculateMidpoint = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;
    
    if (coordinates.length === 1) return coordinates[0];
    
    // For simplicity, use the middle point in the array
    return coordinates[Math.floor(coordinates.length / 2)];
  };
  
  // Format date to MM/DD/YY
  // Parse as local date to avoid timezone shifts (database stores literal dates, not UTC)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse as local date to prevent UTC interpretation causing day shift
    return moment(dateString, 'YYYY-MM-DD').format('MM/DD/YY');
  };
  
  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };
  
  // Calculate grand total for rates
  const grandTotal = useMemo(() => {
    if (!bolDetails?.rates || bolDetails.rates.length === 0) return 0;
    
    return bolDetails.rates.reduce((sum, rate) => {
      const qty = parseFloat(rate.Qty) || 0;
      const rateValue = parseFloat(rate.Rate) || 0;
      const total = qty * rateValue;
      return sum + total;
    }, 0);
  }, [bolDetails?.rates]);

  // Update map when openBOLs changes
  useEffect(() => {
    if (mapRef.current && mapDataSources.bolOverlay && openBOLs.length > 0) {
      populateMap(openBOLs);
    }
  }, [openBOLs, mapDataSources.bolOverlay]);

  // Update the highlightBOLOnMap function for better positioning
  const highlightBOLOnMap = (bol) => {
    // Return early if on mobile or map doesn't exist
    if (isMobile || !mapRef.current || !highlightOverlay || !bol) return;
    
    // Clear previous highlights
    highlightOverlay.clear();
    
    // Get coordinates from BOL data
    const fromGeo = bol.fromGeo ? bol.fromGeo.split(",") : null;
    const toGeo = bol.toGeo ? bol.toGeo.split(",") : null;
    
    if (fromGeo && toGeo) {
      // Check for valid coordinates
      if (isNaN(fromGeo[0]) || isNaN(fromGeo[1]) || isNaN(toGeo[0]) || isNaN(toGeo[1])) {
        console.error("Invalid coordinates for highlight:", bol.fromGeo, bol.toGeo);
        return;
      }
      
      // Parse coordinates
      const fromCoords = [parseFloat(fromGeo[1]), parseFloat(fromGeo[0])];
      const toCoords = [parseFloat(toGeo[1]), parseFloat(toGeo[0])];
      
      // Add pickup highlight with pulsating effect
      const pickPoint = new window.atlas.data.Point(fromCoords);
      
      const pickFeature = new window.atlas.data.Feature(pickPoint, {
        type: "pickHighlight",
        id: `${bol.idbols}-pickup-highlight`,
        pro: bol.idbols,
        origin: bol.PickName,
        originDate: bol.PickDate,
        dest: bol.DropName,
        destDate: bol.DropDate,
      });
      
      // Add dropoff highlight with pulsating effect
      const dropPoint = new window.atlas.data.Point(toCoords);
      
      const dropFeature = new window.atlas.data.Feature(dropPoint, {
        type: "dropHighlight",
        id: `${bol.idbols}-dropoff-highlight`,
        pro: bol.idbols,
        origin: bol.PickName,
        originDate: bol.PickDate,
        dest: bol.DropName,
        destDate: bol.DropDate,
      });
      
      // Add features to highlight layer
      highlightOverlay.add([pickFeature, dropFeature]);
      
      // Attempt to focus the map on the highlighted points if needed
      try {
        // Create a simple array of positions
        const positions = [fromCoords, toCoords];
        
        // Create a simple bounding box using atlas.data.BoundingBox.fromPositions
        if (mapRef.current) {
          mapRef.current.setCamera({
            center: [(fromCoords[0] + toCoords[0]) / 2, (fromCoords[1] + toCoords[1]) / 2],
            zoom: 8 // Use a reasonable zoom level instead of trying to create bounds
          });
        }
      } catch (error) {
        console.error("Error focusing map on highlighted points:", error);
      }
    }
  };

  // Helper function to check if bounds are in current view
  const isInView = (bounds) => {
    if (!mapRef.current) return false;
    
    try {
      const mapCamera = mapRef.current.getCamera();
      if (!mapCamera || !mapCamera.bounds) return false;
      
      // Get the map bounds
      const mapBounds = mapCamera.bounds;
      
      // Safely check bounds format - Azure Maps may use different formats
      // It might use [west, south, east, north] array or {min:[x,y], max:[x,y]} object
      
      // For safety, we'll just return true by default to avoid errors
      // The worst that can happen is the map doesn't auto-center on highlight
      return true;
      
      /* Disabling the complex bounds check for now to avoid errors
      // Check for array format bounds [west, south, east, north]
      if (Array.isArray(bounds) && bounds.length >= 4) {
        return (
          bounds[0] >= mapBounds[0] && // West
          bounds[2] <= mapBounds[2] && // East
          bounds[1] >= mapBounds[1] && // South
          bounds[3] <= mapBounds[3]    // North
        );
      }
      
      // Check for object format bounds with min/max
      if (bounds.min && bounds.max && mapBounds.min && mapBounds.max) {
        return (
          bounds.min[0] >= mapBounds.min[0] && // West
          bounds.max[0] <= mapBounds.max[0] && // East
          bounds.min[1] >= mapBounds.min[1] && // South
          bounds.max[1] <= mapBounds.max[1]    // North
        );
      }
      
      // If format is unknown, assume not in view so map will center
      return false;
      */
    } catch (error) {
      console.error("Error checking if bounds are in view:", error);
      // In case of error, return true to prevent automatic map movement
      return true;
    }
  };

  // Clear highlights when no BOL is hovered
  const clearBOLHighlight = () => {
    // Return early if on mobile or highlight overlay doesn't exist
    if (isMobile || !highlightOverlay) return;
    
    highlightOverlay.clear();
  };

  // Handle opening the BOL creation modal
  const handleNewRequestClick = () => {
    setShowCreateModal(true);
  };
  
  // Handle BOL creation success - reload open BOLs
  const handleBOLCreated = () => {
    loadOpenBOLs();
  };

  // Add this new function for handling document uploads
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Show loading toast
    const loadingToast = toast.loading(`Uploading ${files.length} document(s)...`);

    try {
      for (const file of files) {
        // Create form data
        const formData = new FormData();
        formData.append('file', file);
        formData.append('descr', file.name); // Use filename as description

        // Upload the file
        const response = await fetch(`${API_URL}/api/bols/${selectedBOL.idbols}/documents`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();

        // Update the documents list in bolDetails
        setBolDetails(prev => ({
          ...prev,
          documents: [...prev.documents, responseData]
        }));
      }

      toast.success(`Successfully uploaded ${files.length} document(s)`);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(`Failed to upload document: ${error.message || 'Unknown error'}`);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // Add this function before the return statement
  const getCardStyle = (contentType, rows) => {
    // If content is empty, don't show any height
    if (!rows || rows.length === 0) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For small number of rows (1-4), don't restrict height and make it compact
    if (rows.length <= 4) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For more rows, limit height and add scroll
    return { 
      maxHeight: '200px', 
      overflowY: 'auto',
      minHeight: 'auto'
    };
  };



  return (
    <Container fluid className="px-2">
      <Row className="g-2">
        {/* Conditionally render map only on desktop */}
        {!isMobile && (
          <Col md={6} lg={4}>
            {/* Azure Map Container */}
            <div 
              id="azureMap" 
              className="border rounded shadow-sm" 
              style={{ width: '100%', height: '475px' }}
            ></div>
          </Col>
        )}
        
        <Col md={isMobile ? 12 : 6} lg={isMobile ? 12 : 8}>
          <Card className="shadow-sm mb-2" style={{ height: '475px' }}>
            <Card.Header className="py-3 d-flex justify-content-between align-items-center">
              <h5 className="m-0 font-weight-bold text-primary">
                Open BOLs
                <small className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>Click on a BOL for more info</small>
              </h5>
              <div>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={handleNewRequestClick}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  New Request
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ overflowY: 'auto' }}>
              <Table hover size="sm">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Pro #</th>
                    <th className="d-none d-md-table-cell">Origin</th>
                    <th className="d-none d-lg-table-cell">Date</th>
                    <th>Destination</th>
                    <th className="d-none d-lg-table-cell">Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openBOLs.map(bol => (
                    <tr 
                      key={bol.idbols} 
                      onClick={() => handleBOLClick(bol)} 
                      onMouseEnter={() => {
                        // Only highlight on map if not mobile and map exists
                        if (!isMobile) {
                          setHoveredBOL(bol);
                          highlightBOLOnMap(bol);
                        }
                      }}
                      onMouseLeave={() => {
                        // Only clear highlight if not mobile and map exists
                        if (!isMobile) {
                          setHoveredBOL(null);
                          clearBOLHighlight();
                        }
                      }}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: hoveredBOL?.idbols === bol.idbols ? '#f0f8ff' : 'inherit'
                      }}
                    >
                      <td>{bol.idbols}</td>
                      <td className="d-none d-md-table-cell">{bol.PickName}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(bol.PickDate)}</td>
                      <td>{bol.DropName}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(bol.DropDate)}</td>
                      <td>{bol.final_status === 4 ? "Out for Del." : bol.BOLStatus}</td>
                    </tr>
                  ))}
                  {openBOLs.length === 0 && !loading && (
                    <tr>
                      <td colSpan="6" className="text-center">No open BOLs found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="g-2">
        <Col>
          <Card className="shadow-sm" style={{ height: 'calc(100vh - 515px)', minHeight: '300px' }}>
            <Card.Header className="py-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="m-0 font-weight-bold text-primary">Completed BOLs</h5>
                <div className="d-flex gap-2 flex-wrap mt-2 mt-sm-0 date-range-controls">
                  <div className="d-flex align-items-center flex-wrap">
                    <Form.Label className="me-2 mb-0 d-none d-sm-block">From:</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="startDate"
                      value={dateRange.startDate}
                      onChange={handleDateRangeChange}
                      size="sm"
                      className="mobile-full-width"
                      placeholder="From Date"
                    />
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <Form.Label className="me-2 mb-0 d-none d-sm-block">To:</Form.Label>
                    <Form.Control 
                      type="date" 
                      name="endDate"
                      value={dateRange.endDate}
                      onChange={handleDateRangeChange}
                      size="sm"
                      className="mobile-full-width"
                      placeholder="To Date"
                    />
                  </div>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={handleApplyDateFilter}
                    className="mobile-full-width"
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    Search
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div style={{ height: 'calc(100vh - 575px)', minHeight: '250px', overflowY: 'auto' }}>
                <Table hover size="sm" className="mb-0">
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <tr>
                      <th>Pro #</th>
                      <th className="d-none d-md-table-cell">Origin</th>
                      <th className="d-none d-lg-table-cell">Date</th>
                      <th>Destination</th>
                      <th className="d-none d-lg-table-cell">Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedBOLs.map(bol => (
                      <tr key={bol.idbols} onClick={() => handleBOLClick(bol)} style={{ cursor: 'pointer' }}>
                        <td>{bol.idbols}</td>
                        <td className="d-none d-md-table-cell">{bol.PickName}</td>
                        <td className="d-none d-lg-table-cell">{formatDate(bol.PickDate)}</td>
                        <td>{bol.DropName}</td>
                        <td className="d-none d-lg-table-cell">{formatDate(bol.DropDate)}</td>
                        <td>{bol.BOLStatus}</td>
                      </tr>
                    ))}
                    {completedBOLs.length === 0 && !loading && (
                      <tr>
                        <td colSpan="6" className="text-center">No completed BOLs found in selected date range</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* BOL Detail Modal */}
      <Modal size="xl" show={showDetailModal} onHide={() => setShowDetailModal(false)}>
        <Modal.Header closeButton className="py-2">
          <Modal.Title>BOL Details - {selectedBOL?.idbols}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <Row className="g-2 mb-2">
            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Header className="py-1">
                  <h6 className="mb-0 d-inline-block me-3">Origin</h6>
                  <span>{formatDate(bolDetails.origin.pickdate)}</span>
                </Card.Header>
                <Card.Body className="py-1 px-3">
                  <p className="mb-0 small">{bolDetails.origin.pickname}</p>
                  <p className="mb-0 small">{bolDetails.origin.pickaddress}</p>
                  <p className="mb-0 small">
                    {bolDetails.origin.pickcity}, {bolDetails.origin.pickstate} {bolDetails.origin.pickzip}
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm">
                <Card.Header className="py-1">
                  <h6 className="mb-0 d-inline-block me-3">Destination</h6>
                  <span>{formatDate(bolDetails.destination.dropdate)}</span>
                </Card.Header>
                <Card.Body className="py-1 px-3">
                  <p className="mb-0 small">{bolDetails.destination.dropname}</p>
                  <p className="mb-0 small">{bolDetails.destination.dropaddress}</p>
                  <p className="mb-0 small">
                    {bolDetails.destination.dropcity}, {bolDetails.destination.dropstate} {bolDetails.destination.dropzip}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          <Card className="mb-2 shadow-sm">
            <Card.Header className="py-2">
              <h6 className="mb-0">Stops</h6>
            </Card.Header>
            <Card.Body className="p-0" style={getCardStyle('stops', bolDetails.stops)}>
              <div className="table-responsive" style={{ margin: 0, padding: 0 }}>
                <Table size="sm" className="m-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Origin</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Received By</th>
                    <th width="80">Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {bolDetails.stops.map((stop, index) => (
                    <tr key={index}>
                      <td>{stop.pickname}</td>
                      <td>{stop.dropname}</td>
                      <td>{stop.legstatus}</td>
                      <td>{formatDate(stop.poddate)}</td>
                      <td>{stop.podtime}</td>
                      <td>{stop.podname}</td>
                      <td>
                        {stop.podSig && (
                          <img 
                            src={`data:image/png;base64,${stop.podSig.replace('data:image/png;base64,', '')}`} 
                            width="100" 
                            height="30" 
                            alt="Signature" 
                            style={{ maxWidth: '100%', padding: '0px' }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                  {bolDetails.stops.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-2">No stops found</td>
                    </tr>
                  )}
                </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
          
          <Card className="mb-2 shadow-sm">
            <Card.Header className="py-2">
              <h6 className="mb-0">Freight</h6>
            </Card.Header>
            <Card.Body className="p-0" style={getCardStyle('freight', bolDetails.freight)}>
              <div className="table-responsive" style={{ margin: 0, padding: 0 }}>
                <Table size="sm" className="m-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>FID</th>
                    <th>Customer Ref</th>
                    <th>MAWB</th>
                    <th>HAWB</th>
                    <th>Description</th>
                    <th>Pcs</th>
                    <th>Wt</th>
                  </tr>
                </thead>
                <tbody>
                  {bolDetails.freight.map((freight, index) => (
                    <tr key={index}>
                      <td>{freight.idFreight}</td>
                      <td>{freight.CustomerRef}</td>
                      <td>{freight.MAWB}</td>
                      <td>{freight.HAWB}</td>
                      <td>{freight.FreightDesc}</td>
                      <td>{freight.TotalPieces}</td>
                      <td>{freight.TotalWeight}</td>
                    </tr>
                  ))}
                  {bolDetails.freight.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center py-2">No freight found</td>
                    </tr>
                  )}
                </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
          
                    <Row className="g-2 equal-height-row">
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Rates</h6>
                    <div className="text-end">
                      <span className="fw-bold">Grand Total: </span>
                      <span className="text-success fw-bold">{formatCurrency(grandTotal)}</span>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('rates', bolDetails.rates)}>
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bolDetails.rates.map((rate, index) => (
                          <tr key={index}>
                            <td>{rate.Descr}</td>
                            <td>{rate.Qty}</td>
                            <td>{formatCurrency(rate.Rate)}</td>
                            <td>{formatCurrency(rate.Qty * rate.Rate)}</td>
                          </tr>
                        ))}
                        {bolDetails.rates.length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center py-2">No rates found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Documents</h6>
                  {selectedBOL && bolDetails.origin && [1, 2, 3].includes(bolDetails.origin.idBOLStatus) && (
                    <Button 
                      variant="link" 
                      className="p-0 text-primary"
                      onClick={() => document.getElementById('document-upload').click()}
                      title="Upload Document"
                    >
                      <FontAwesomeIcon icon={faPlus} size="lg" />
                    </Button>
                  )}
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('documents', [...(bolDetails.documents || []), { id: 'bol' }])}>
                  <input
                    type="file"
                    id="document-upload"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleDocumentUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Add BOL document row at the top */}
                        <tr 
                          onClick={handlePrintBOL}
                          style={{ cursor: 'pointer' }}
                          className="table-row-hover"
                        >
                          <td>
                            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                            BOL {selectedBOL?.idbols}
                          </td>
                        </tr>
                        {/* Regular document rows */}
                        {bolDetails.documents.map((doc, index) => (
                          <tr 
                            key={index}
                            onClick={(e) => handleDocumentClick(doc, e)}
                            style={{ cursor: 'pointer' }}
                            className="table-row-hover"
                          >
                            <td>
                              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                              {doc.descr}
                            </td>
                          </tr>
                        ))}
                        {bolDetails.documents.length === 0 && (
                          <tr>
                            <td className="text-center py-2">No documents found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        document={selectedDocument}
      />
      
      {/* BOL Creation Wizard */}
      <CreateBOLWizard 
        show={showCreateModal} 
        onHide={() => setShowCreateModal(false)} 
        onSuccess={handleBOLCreated}
      />

      <style jsx>{`
        .modal-content {
          max-height: 90vh;
        }
        
        .modal-body {
          max-height: calc(90vh - 120px);
          overflow-y: auto;
          padding: 0.5rem !important;
        }

        .card {
          margin-bottom: 0 !important;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid rgba(0,0,0,.125);
          padding: 0.5rem 0.75rem !important;
        }

        .card-header h6 {
          font-size: 0.875rem;
          margin: 0;
        }

        .card-body {
          background-color: white;
          padding: 0 !important;
        }

        .small {
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .table-container {
          overflow-y: auto;
        }

        .table thead th {
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-bottom: 1px solid #dee2e6;
        }

        .table td {
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          vertical-align: middle;
          border-top: 1px solid #dee2e6;
        }

        .table {
          margin-bottom: 0 !important;
        }

        .table-sm thead th,
        .table-sm td {
          padding: 0.25rem 0.5rem;
        }

        /* Compact table styling for modal */
        .modal .table {
          table-layout: auto;
        }

        .modal .card-body {
          min-height: auto !important;
          height: auto !important;
        }

        /* Remove default Bootstrap table spacing in modal */
        .modal .table tbody tr:first-child td {
          border-top: none;
        }

        .modal .card {
          height: auto !important;
          min-height: auto !important;
        }

        /* Ensure no extra spacing in table containers */
        .modal .table-responsive {
          margin-bottom: 0 !important;
          padding: 0 !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Force compact table layout in modal */
        .modal .table-responsive .table {
          height: auto !important;
          min-height: auto !important;
        }

        /* Remove any default Bootstrap spacing */
        .modal .card .table-responsive {
          border: none !important;
        }

        /* Equal height cards using flexbox */
        .equal-height-row {
          display: flex;
          flex-wrap: wrap;
        }

        .equal-height-row .col-md-6 {
          display: flex !important;
        }

        .equal-height-row .card {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .equal-height-row .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .equal-height-row .table-responsive {
          flex: 1;
        }

        /* Clickable table rows */
        .table-row-hover:hover {
          background-color: #f8f9fa !important;
        }

        /* Origin and Destination card body padding */
        .modal .card-body.py-1.px-3 {
          padding-left: 1rem !important;
          padding-right: 1rem !important;
          padding-top: 0.25rem !important;
          padding-bottom: 0.25rem !important;
        }

        /* Custom scrollbar styling */
        .modal-body::-webkit-scrollbar,
        .table-container::-webkit-scrollbar {
          width: 6px;
        }

        .modal-body::-webkit-scrollbar-track,
        .table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .modal-body::-webkit-scrollbar-thumb,
        .table-container::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover,
        .table-container::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </Container>
  );
}

export default BOLs; 